/**
 * useDeviceLimit - Device-aware pagination limit hook
 * 
 * Detects the device type (mobile vs desktop) and returns
 * appropriate pagination limits for optimal UX.
 * 
 * - Desktop (>=768px): 50 items per page
 * - Mobile (<768px): 30 items per page
 * 
 * @module hooks/useDeviceLimit
 */
"use client";

import { useState, useEffect, useCallback } from "react";

// ============================================================================
// Types
// ============================================================================

export interface DeviceLimits {
  /** Items per page based on device */
  readonly pageSize: number;
  /** Whether current viewport is desktop */
  readonly isDesktop: boolean;
  /** Whether current viewport is mobile */
  readonly isMobile: boolean;
  /** Current viewport width in pixels */
  readonly viewportWidth: number;
}

// ============================================================================
// Constants
// ============================================================================

/** Desktop page size (>=768px viewport) */
const DESKTOP_PAGE_SIZE = 50;

/** Mobile page size (<768px viewport) */
const MOBILE_PAGE_SIZE = 30;

/** Breakpoint for mobile/desktop detection */
const MOBILE_BREAKPOINT = 768;

/** Default values for SSR (assume desktop) */
const SSR_DEFAULTS: DeviceLimits = {
  pageSize: DESKTOP_PAGE_SIZE,
  isDesktop: true,
  isMobile: false,
  viewportWidth: 1024
};

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to detect device type and return appropriate pagination limits
 * 
 * @example
 * ```tsx
 * const { pageSize, isMobile, isDesktop } = useDeviceLimit();
 * 
 * // pageSize will be 30 on mobile, 50 on desktop
 * const { data } = useArtworks({ limit: pageSize });
 * ```
 */
export function useDeviceLimit(): DeviceLimits {
  const [limits, setLimits] = useState<DeviceLimits>(SSR_DEFAULTS);

  /**
   * Calculate limits based on current viewport width
   */
  const calculateLimits = useCallback((): DeviceLimits => {
    if (typeof window === "undefined") {
      return SSR_DEFAULTS;
    }

    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < MOBILE_BREAKPOINT;
    const isDesktop = !isMobile;
    const pageSize = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;

    return {
      pageSize,
      isDesktop,
      isMobile,
      viewportWidth
    };
  }, []);

  /**
   * Update limits on mount and window resize
   */
  useEffect(() => {
    // Initial calculation
    setLimits(calculateLimits());

    // Debounced resize handler
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setLimits(calculateLimits());
      }, 150); // Debounce 150ms
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [calculateLimits]);

  return limits;
}

export default useDeviceLimit;
